import React from 'react';
import PropTypes from 'prop-types';
import styled, { injectGlobal } from 'styled-components';
import { withTranslation } from 'react-i18next';
import {
  SortableContainer, SortableElement, SortableHandle, arrayMove
} from 'react-sortable-hoc';
import Loading from 'common/components/atoms/Loading';
import { THIN_SCROLLBAR } from 'common/styles/listUI';
import Popover from 'common/components/molecules/Popover';
import MenuSelect from 'common/components/molecules/MenuSelect';

const INK = '#383838';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const LINE_2 = '#eff0ee';
const BG = '#f6f7f4';
const BLUE = '#2f6fd0';
const BLUE_T = '#eaf1fb';
const GREEN = '#6c8a2e';
const CRITICAL = '#d92b2b';
const WARNING = '#e8890c';

// Insight cards. KPIs read from `asset_metrics`; widget/donut rows read from
// their `source` breakdown on the summary response. `filterId` (when set)
// makes the card/row clickable and applies the matching queue filter.
const CARDS = [
  {
    id: 'alarms_not_reviewed',
    kind: 'kpi',
    metric: 'alarms_not_reviewed',
    labelKey: 'assets.insights.alarmsNotReviewed',
    def: 'Alarms not reviewed',
    filterId: 'unreviewed',
    bad: true
  },
  {
    id: 'no_sensors',
    kind: 'kpi',
    metric: 'no_sensors',
    labelKey: 'assets.insights.noSensors',
    def: 'Assets without sensors',
    filterId: 'nosensors',
    bad: true
  },
  {
    id: 'poor_wifi',
    kind: 'kpi',
    metric: 'poor_wifi',
    labelKey: 'assets.insights.poorWifi',
    def: 'Assets with poor WiFi',
    filterId: 'poorwifi',
    bad: true
  },
  {
    id: 'setup_avg',
    kind: 'kpi',
    metric: 'setup_avg',
    labelKey: 'assets.insights.setupAvg',
    def: 'Setup completion (avg)',
    filterId: null
  },
  {
    id: 'asset_setup',
    kind: 'widget',
    source: 'setup_breakdown',
    titleKey: 'assets.insights.assetSetup',
    def: 'Asset setup',
    detailWidget: 'incomplete_asset_setup_breakdown',
    // No setup row is currently clickable, and each is unwired for its own
    // reason — none of them is an oversight:
    //  - baseline: shares a predicate with the `baselines_status` criterion
    //    (both derive from process_baseline_data) but NOT a population. This row
    //    counts every in-scope asset, whereas baselines_status is a label-only
    //    ARA reason that does not by itself pull an asset into the attention
    //    queue. Wiring it would make the number you click disagree with the
    //    number you land on.
    //  - alarms: means "alarm thresholds not configured", which is NOT the
    //    `alarms` criterion (open alarm events).
    //  - speed_info: folds into `missing_asset_info` together with other inputs,
    //    so filtering on it would over-select.
    //  - others: has no corresponding criterion at all.
    // Making any of these clickable needs an attention-scoped count from the
    // summary endpoint first.
    rows: [
      { key: 'baseline', labelKey: 'assets.insights.setupBaseline', def: 'Baselines', color: WARNING, filterId: null },
      { key: 'alarms', labelKey: 'assets.insights.setupAlarms', def: 'Alarm thresholds', color: '#e8b04a', filterId: null },
      { key: 'speed_info', labelKey: 'assets.insights.setupSpeed', def: 'Speed info', color: INK_3, filterId: null },
      { key: 'others', labelKey: 'assets.insights.setupOthers', def: 'Other info', color: '#c9ccc4', filterId: null }
    ]
  },
  {
    id: 'active_alarms',
    kind: 'widget',
    source: 'alarm_breakdown',
    titleKey: 'assets.insights.activeAlarms',
    def: 'Active alarms',
    detailWidget: 'open_events_breakdown',
    rows: [
      { key: 'critical', labelKey: 'assets.insights.alarmCritical', def: 'Critical', color: CRITICAL, filterId: null },
      { key: 'warning', labelKey: 'assets.insights.alarmWarning', def: 'Warning', color: WARNING, filterId: null },
      { key: 'unreviewed', labelKey: 'assets.insights.alarmUnreviewed', def: 'Unreviewed', color: INK_3, filterId: 'unreviewed' }
    ]
  },
  {
    id: 'health_distribution',
    kind: 'widget',
    source: 'health_distribution',
    titleKey: 'assets.insights.healthDistribution',
    def: 'Health score distribution',
    rows: [
      { key: 'critical', labelKey: 'assets.insights.healthCritical', def: 'Critical <4', color: CRITICAL, filterId: null },
      { key: 'warning', labelKey: 'assets.insights.healthWarning', def: 'Warning <7', color: WARNING, filterId: null },
      { key: 'normal', labelKey: 'assets.insights.healthNormal', def: 'Normal ≥7', color: GREEN, filterId: null }
    ]
  },
  {
    id: 'asset_types',
    kind: 'donut',
    source: 'asset_type_breakdown',
    titleKey: 'assets.insights.assetTypes',
    def: 'Asset types',
    detailWidget: 'vfd_breakdown',
    segments: [
      { key: 'vfd', labelKey: 'assets.insights.typeVfd', def: 'VFD', color: GREEN },
      { key: 'non_vfd', labelKey: 'assets.insights.typeNonVfd', def: 'Non-VFD', color: INK_3 },
      { key: 'other', labelKey: 'assets.insights.typeOther', def: 'Other', color: '#d2d6cd' }
    ]
  }
];

const TREND_WINDOWS = [
  { id: '5d', labelKey: 'assets.insights.trend5d', def: '5D', unit: 'days', value: 5 },
  { id: '1m', labelKey: 'assets.insights.trend1m', def: '1M', unit: 'months', value: 1 },
  { id: '3m', labelKey: 'assets.insights.trend3m', def: '3M', unit: 'months', value: 3 },
  { id: '6m', labelKey: 'assets.insights.trend6m', def: '6M', unit: 'months', value: 6 },
  { id: '1y', labelKey: 'assets.insights.trend1y', def: '1Y', unit: 'months', value: 12 }
];

const Drawer = styled.div`
  background: ${BG};
  border-bottom: 1px solid ${LINE};
  padding: 12px 20px;
  overflow-x: auto;
  overflow-y: visible;
  overscroll-behavior-x: contain;
  ${THIN_SCROLLBAR}
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  position: relative;
`;

const Title = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${INK_2};
`;

const Hint = styled.span`
  font-size: 11px;
  color: ${INK_3};
`;

const Customize = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${GREEN};
  cursor: pointer;
  border: 1px solid ${LINE};
  border-radius: 7px;
  padding: 4px 10px;
  background: white;

  &:hover { border-color: ${GREEN}; background: #eef3e2; }
`;

const Pop = styled.div`
  min-width: 210px;
  background: white;
  border: 1px solid ${LINE};
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(40, 44, 36, 0.16);
  padding: 6px;
  max-height: 340px;
  overflow-y: auto;
  ${THIN_SCROLLBAR}
`;

const PopTitle = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${INK_3};
  padding: 6px 8px 4px;
`;

const PopItem = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 12.5px;
  color: ${INK};
  cursor: pointer;

  &:hover { background: ${LINE_2}; }

  &:focus-visible {
    outline: 2px solid ${BLUE};
    outline-offset: -2px;
  }
`;

const Cb = styled.span`
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 1.5px solid ${INK_3};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${props => props.on && `background: ${GREEN}; border-color: ${GREEN};`}

  &::after {
    content: '';
    display: ${props => (props.on ? 'block' : 'none')};
    width: 7px;
    height: 4px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg) translate(0, -1px);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
  min-height: 92px;
`;

const Card = styled.div`
  position: relative;
  background: white;
  border: 1px solid ${props => (props.active ? BLUE : LINE)};
  border-radius: 10px;
  padding: 9px 12px;
  flex-shrink: 0;
  user-select: none;
  transition: box-shadow 0.12s, border-color 0.12s;
  ${props => props.active && `box-shadow: 0 0 0 1px ${BLUE};`}
  ${props => props.kind === 'kpi' && 'width: 158px;'}
  ${props => props.kind === 'widget' && 'min-width: 214px;'}
  ${props => props.clickable && 'cursor: pointer;'}

  &:hover {
    ${props => props.clickable && `border-color: ${BLUE};`}
  }

  &:focus-visible {
    outline: 2px solid ${BLUE};
    outline-offset: 2px;
  }
`;

const GripEl = styled.span`
  position: absolute;
  top: 6px;
  right: 7px;
  color: #d2d6cd;
  font-size: 11px;
  letter-spacing: -2px;
  cursor: grab;

  &:hover { color: ${INK_3}; }
  &:active { cursor: grabbing; }
`;

// react-sortable-hoc drag handle (drag from the grip only, so card clicks still fire).
const Grip = SortableHandle(() => <GripEl>⠿</GripEl>);

// While dragging, react-sortable-hoc clones the card into a floating helper
// (lifted look); the source slot collapses, leaving the drop gap.
// eslint-disable-next-line no-unused-expressions, chai-friendly/no-unused-expressions
injectGlobal`
  .insights-card-helper {
    z-index: 4000;
    cursor: grabbing !important;
    box-shadow: 0 14px 30px rgba(40, 44, 36, 0.22);
    border: 1px solid ${BLUE} !important;
    border-radius: 10px;
    background: white;
    opacity: 0.98;
    transform: scale(1.03);
  }
`;

const KpiValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${INK};
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const Delta = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: ${(props) => {
    if (props.tone === 'bad') return CRITICAL;
    if (props.tone === 'good') return GREEN;
    return INK_3;
  }};
`;

const KpiLabel = styled.div`
  font-size: 11px;
  color: ${INK_2};
  margin-top: 4px;
  line-height: 1.3;
`;

const WidgetHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const WidgetTitle = styled.h4`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${INK_2};
  margin: 0;
`;

const Expand = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${INK_3};
  padding: 0 2px;
  display: inline-flex;
  align-items: center;

  &:hover { color: ${BLUE}; }
  svg { width: 12px; height: 12px; }
`;

const ExpandIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

const EmptyRow = styled.div`
  font-size: 11.5px;
  color: ${INK_3};
  padding: 6px 0;
`;

const WRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
  font-size: 11.5px;
  color: ${props => (props.active ? BLUE : INK_2)};
  font-weight: ${props => (props.active ? 700 : 400)};
  border-radius: 5px;
  padding: 1px 4px;
  margin-left: -4px;
  ${props => props.active && `background: ${BLUE_T};`}
  ${props => props.clickable && 'cursor: pointer;'}

  &:hover { ${props => props.clickable && `background: ${BLUE_T};`} }

  &:focus-visible {
    outline: 2px solid ${BLUE};
    outline-offset: 1px;
  }

  .wl { width: 72px; flex-shrink: 0; }
  .wbar { flex: 1; height: 6px; background: ${LINE_2}; border-radius: 3px; overflow: hidden; }
  .wfill { display: block; height: 100%; }
  .wn { width: 20px; text-align: right; font-weight: 700; color: ${INK}; }
`;

const Donut = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  svg { transform: rotate(0deg); flex-shrink: 0; }
  .leg { font-size: 11px; color: ${INK_2}; line-height: 1.7; white-space: nowrap; }
  .leg .sw { display: inline-block; width: 8px; height: 8px; border-radius: 2px; margin-right: 5px; }
`;

const Empty = styled.div`
  padding: 24px;
  color: ${INK_3};
  font-size: 12.5px;
`;

// Fire a click-equivalent handler on Enter/Space so keyboard users can operate
// the div/span-based interactive elements (cards, rows, popover items).
const onActivateKey = handler => (e) => {
  if (!handler) return;
  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
    e.preventDefault();
    handler(e);
  }
};

const metricValue = (card, data) => {
  const metrics = (data && data.asset_metrics) || {};
  if (card.metric === 'setup_avg') {
    const avg = data && data.avg_setup_completion_percent;
    return avg == null ? '—' : `${Math.round(avg)}%`;
  }
  return metrics[card.metric] != null ? metrics[card.metric] : 0;
};

const sourceValue = (source, key, data) => {
  const bucket = (data && data[source]) || {};
  return bucket[key] || 0;
};

const renderDelta = (card, data, t) => {
  if (!card.metric || card.metric === 'setup_avg') return null;
  const trends = (data && data.asset_metric_trends) || {};
  const trend = trends[card.metric];
  if (!trend || trend.percent_change == null) return null;
  const pct = trend.percent_change;
  let tone = '';
  let glyph = '→';
  if (pct > 0) {
    glyph = '▲';
    tone = card.bad ? 'bad' : 'good';
  } else if (pct < 0) {
    glyph = '▼';
    tone = card.bad ? 'good' : 'bad';
  }
  return (
    <Delta tone={tone} title={t('assets.insights.vsPrevious', { defaultValue: 'vs previous period' })}>
      {`${glyph} ${Math.abs(Math.round(pct))}%`}
    </Delta>
  );
};

const renderDonut = (card, ctx) => {
  const { data, onExpand, t } = ctx;
  const title = t(card.titleKey, { defaultValue: card.def });
  const bucket = (data && data[card.source]) || {};
  const segs = card.segments.map(s => ({ ...s, value: bucket[s.key] || 0 }));
  const total = segs.reduce((sum, s) => sum + s.value, 0) || 1;

  let cumulative = 0;
  const arcs = segs.map((s) => {
    const pct = (s.value / total) * 100;
    const arc = (
      <circle
        key={s.key}
        cx="21"
        cy="21"
        r="15.9155"
        fill="none"
        stroke={s.color}
        strokeWidth="6"
        strokeDasharray={`${pct} ${100 - pct}`}
        strokeDashoffset={25 - cumulative}
      />
    );
    cumulative += pct;
    return arc;
  });

  return (
    <Card kind="widget">
      <Grip />
      <WidgetHead>
        <WidgetTitle>{title}</WidgetTitle>
        {card.detailWidget && (
          <Expand
            type="button"
            aria-label={t('assets.insights.viewDetailsFor', { defaultValue: 'View detailed breakdown: {{title}}', title })}
            title={t('assets.insights.viewDetails', { defaultValue: 'View detailed breakdown' })}
            onClick={(e) => { e.stopPropagation(); onExpand(card.detailWidget, title); }}
          >
            <ExpandIcon />
          </Expand>
        )}
      </WidgetHead>
      <Donut>
        <svg width="56" height="56" viewBox="0 0 42 42">
          <circle cx="21" cy="21" r="15.9155" fill="none" stroke={LINE_2} strokeWidth="6" />
          {arcs}
        </svg>
        <div className="leg">
          {segs.filter(s => s.value > 0).map(s => (
            <div key={s.key}>
              <span className="sw" style={{ background: s.color }} />
              {`${t(s.labelKey, { defaultValue: s.def })} ${s.value}`}
            </div>
          ))}
        </div>
      </Donut>
    </Card>
  );
};

const renderCardContent = (card, ctx) => {
  const {
    data, activeInsightId, onApplyInsight, onExpand, t
  } = ctx;
  if (card.kind === 'kpi') {
    const clickable = Boolean(card.filterId);
    const active = clickable && activeInsightId === card.filterId;
    const applyFilter = () => onApplyInsight(card.filterId);
    return (
      <Card
        kind="kpi"
        clickable={clickable}
        active={active}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-pressed={clickable ? active : undefined}
        aria-label={clickable
          ? t('assets.insights.filterByKpi', {
            defaultValue: 'Filter queue by {{label}}',
            label: t(card.labelKey, { defaultValue: card.def })
          })
          : undefined}
        onClick={clickable ? applyFilter : undefined}
        onKeyDown={clickable ? onActivateKey(applyFilter) : undefined}
      >
        <Grip />
        <KpiValue>
          {metricValue(card, data)}
          {renderDelta(card, data, t)}
        </KpiValue>
        <KpiLabel>{t(card.labelKey, { defaultValue: card.def })}</KpiLabel>
      </Card>
    );
  }
  if (card.kind === 'donut') {
    return renderDonut(card, ctx);
  }

  // Dynamic compact: only show rows that actually have data.
  const rows = card.rows
    .map(r => ({ ...r, value: sourceValue(card.source, r.key, data) }))
    .filter(r => r.value > 0);
  const maxVal = Math.max(1, ...rows.map(r => r.value));
  const title = t(card.titleKey, { defaultValue: card.def });

  return (
    <Card kind="widget">
      <Grip />
      <WidgetHead>
        <WidgetTitle>{title}</WidgetTitle>
        {card.detailWidget && (
          <Expand
            type="button"
            aria-label={t('assets.insights.viewDetailsFor', { defaultValue: 'View detailed breakdown: {{title}}', title })}
            title={t('assets.insights.viewDetails', { defaultValue: 'View detailed breakdown' })}
            onClick={(e) => { e.stopPropagation(); onExpand(card.detailWidget, title); }}
          >
            <ExpandIcon />
          </Expand>
        )}
      </WidgetHead>
      {rows.length === 0 && (
        <EmptyRow>{t('assets.insights.noData', { defaultValue: 'Nothing to show' })}</EmptyRow>
      )}
      {rows.map((r) => {
        const clickable = Boolean(r.filterId);
        const active = clickable && activeInsightId === r.filterId;
        const width = `${Math.round((r.value / maxVal) * 100)}%`;
        const rowLabel = t(r.labelKey, { defaultValue: r.def });
        const applyRowFilter = () => onApplyInsight(r.filterId);
        return (
          <WRow
            key={r.key}
            clickable={clickable}
            active={active}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-pressed={clickable ? active : undefined}
            aria-label={clickable
              ? t('assets.insights.filterByRow', {
                defaultValue: 'Filter queue by {{label}}', label: rowLabel
              })
              : undefined}
            onClick={clickable ? applyRowFilter : undefined}
            onKeyDown={clickable ? onActivateKey(applyRowFilter) : undefined}
          >
            <span className="wl">{rowLabel}</span>
            <span className="wbar"><span className="wfill" style={{ width, background: r.color }} /></span>
            <span className="wn">{r.value}</span>
          </WRow>
        );
      })}
    </Card>
  );
};

const SortableCard = SortableElement(({ card, ctx }) => (
  <div style={{ display: 'inline-flex' }}>{renderCardContent(card, ctx)}</div>
));

const SortableRow = SortableContainer(({ cards, ctx }) => (
  <Row>
    {cards.map((card, index) => (
      <SortableCard key={card.id} index={index} card={card} ctx={ctx} />
    ))}
  </Row>
));

class InsightsDrawer extends React.Component {
  state = { customizeOpen: false };

  setCustBtnRef = (node) => { this.custBtnEl = node; };

  getCustBtn = () => this.custBtnEl;

  toggleCustomize = () => {
    this.setState(prev => ({ customizeOpen: !prev.customizeOpen }));
  };

  closeCustomize = () => {
    this.setState({ customizeOpen: false });
  };

  // Reorder only the visible cards, keeping hidden ones pinned to their slots.
  onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const { order, hidden, onReorder } = this.props;
    const visibleIds = order.filter(id => !hidden.includes(id));
    const moved = arrayMove(visibleIds, oldIndex, newIndex);
    let vi = 0;
    const newOrder = order.map(id => (hidden.includes(id) ? id : moved[vi++]));
    onReorder(newOrder);
  };

  render() {
    const {
      data, order, hidden, loading, activeInsightId,
      onApplyInsight, onExpand, trendWindowId, onChangeTrendWindow, onToggleHidden, t
    } = this.props;
    const { customizeOpen } = this.state;

    const cardById = CARDS.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
    const visibleCards = order
      .map(id => cardById[id])
      .filter(card => card && !hidden.includes(card.id));
    const ctx = {
      data, activeInsightId, onApplyInsight, onExpand, t
    };

    return (
      <Drawer>
        <Head>
          <Title>{t('assets.insights.title', { defaultValue: 'Insights' })}</Title>
          <Hint>{t('assets.insights.hint', { defaultValue: 'Click any metric to filter the queue · drag to rearrange' })}</Hint>
          <MenuSelect
            compact
            value={trendWindowId}
            onChange={onChangeTrendWindow}
            prefix={t('assets.insights.trendWindow', { defaultValue: 'Trend window' })}
            options={TREND_WINDOWS.map(w => ({
              value: w.id, label: t(w.labelKey, { defaultValue: w.def })
            }))}
          />
          <div style={{ marginLeft: 'auto' }}>
            <Customize
              type="button"
              innerRef={this.setCustBtnRef}
              onClick={this.toggleCustomize}
              aria-haspopup="menu"
              aria-expanded={customizeOpen}
              aria-label={t('assets.insights.customize', { defaultValue: 'Customize' })}
            >
              {`⚙ ${t('assets.insights.customize', { defaultValue: 'Customize' })}`}
            </Customize>
            <Popover open={customizeOpen} getAnchor={this.getCustBtn} onClose={this.closeCustomize} align="right">
              <Pop role="menu" aria-label={t('assets.insights.showHide', { defaultValue: 'Show / hide insights' })}>
                <PopTitle>{t('assets.insights.showHide', { defaultValue: 'Show / hide insights' })}</PopTitle>
                {order.map((id) => {
                  const card = cardById[id];
                  if (!card) return null;
                  const label = card.kind === 'kpi'
                    ? t(card.labelKey, { defaultValue: card.def })
                    : t(card.titleKey, { defaultValue: card.def });
                  const shown = !hidden.includes(id);
                  const toggle = () => onToggleHidden(id);
                  return (
                    <PopItem
                      key={id}
                      role="menuitemcheckbox"
                      tabIndex={0}
                      aria-checked={shown}
                      aria-label={label}
                      onClick={toggle}
                      onKeyDown={onActivateKey(toggle)}
                    >
                      <Cb on={shown} aria-hidden="true" />
                      {label}
                    </PopItem>
                  );
                })}
              </Pop>
            </Popover>
          </div>
        </Head>
        {loading && <Loading />}
        {!loading && visibleCards.length === 0 && (
          <Empty>{t('assets.insights.allHidden', { defaultValue: 'All insights hidden — use Customize to show them.' })}</Empty>
        )}
        {!loading && visibleCards.length > 0 && (
          <SortableRow
            axis="x"
            cards={visibleCards}
            ctx={ctx}
            useDragHandle
            distance={4}
            helperClass="insights-card-helper"
            onSortEnd={this.onSortEnd}
          />
        )}
      </Drawer>
    );
  }
}

InsightsDrawer.propTypes = {
  data: PropTypes.object,
  order: PropTypes.arrayOf(PropTypes.string).isRequired,
  hidden: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  activeInsightId: PropTypes.string,
  trendWindowId: PropTypes.string,
  onApplyInsight: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onToggleHidden: PropTypes.func.isRequired,
  onChangeTrendWindow: PropTypes.func.isRequired,
  onExpand: PropTypes.func,
  t: PropTypes.func.isRequired
};

InsightsDrawer.defaultProps = {
  data: null,
  hidden: [],
  loading: false,
  activeInsightId: null,
  trendWindowId: '3m',
  onExpand: () => {}
};

export { CARDS as INSIGHT_CARDS, TREND_WINDOWS };
export default withTranslation()(InsightsDrawer);
